import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Plus, Search, Trash2, Zap, TrendingUp, Mic, DollarSign, CheckCircle, Clock, AlertCircle, Battery } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { usePiekanalyses } from '@/hooks/usePiekanalyses';
import { useTranscriptions } from '@/hooks/useTranscriptions';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type AnalysisType = 'piekvermogen' | 'transcriptie';

interface CombinedAnalysis {
  id: string;
  name: string;
  type: AnalysisType;
  status: string;
  detail: string;
  created_at: string;
  route: string;
}

const CRMAnalyses: React.FC = () => {
  const navigate = useNavigate();
  const { analyses: piekanalyses, isLoading: isLoadingPiek, deleteAnalysis } = usePiekanalyses();
  const { transcriptions, isLoading: isLoadingTranscriptions } = useTranscriptions();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Combine all analyses
  const combinedAnalyses: CombinedAnalysis[] = [
    ...piekanalyses.map(a => ({
      id: a.id,
      name: a.name,
      type: 'piekvermogen' as const,
      status: 'completed',
      detail: `${a.total_exceedances} overschrijdingen`,
      created_at: a.created_at,
      route: `/crm/analyses/piekvermogen/${a.id}`
    })),
    ...transcriptions.map(t => ({
      id: t.id,
      name: t.title || t.filename,
      type: 'transcriptie' as const,
      status: t.status,
      detail: t.status === 'completed' 
        ? `${t.action_items?.length || 0} actiepunten` 
        : t.status === 'processing' ? 'Verwerken...' : t.status,
      created_at: t.created_at,
      route: `/crm/analyses/transcripties?id=${t.id}`
    }))
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const filteredAnalyses = combinedAnalyses.filter(a =>
    a.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Only allow deletion of piekanalyses for now
  const selectedPiekIds = selectedIds.filter(id => 
    piekanalyses.some(p => p.id === id)
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filteredAnalyses.map(a => a.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(i => i !== id));
    }
  };

  const handleBulkDelete = async () => {
    for (const id of selectedPiekIds) {
      await deleteAnalysis.mutateAsync(id);
    }
    setSelectedIds([]);
    setDeleteDialogOpen(false);
  };

  // Calculate transcription costs
  const monthlyCost = transcriptions
    .filter(t => {
      const createdDate = new Date(t.created_at);
      const now = new Date();
      return createdDate.getMonth() === now.getMonth() && 
             createdDate.getFullYear() === now.getFullYear();
    })
    .reduce((sum, t) => sum + (t.total_cost || 0), 0);

  const isLoading = isLoadingPiek || isLoadingTranscriptions;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-3 w-3 text-green-500" />;
      case 'processing':
      case 'analyzing':
      case 'transcribing':
        return <Clock className="h-3 w-3 text-amber-500" />;
      case 'failed':
        return <AlertCircle className="h-3 w-3 text-destructive" />;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="h-6 w-6" />
            Analyses
          </h1>
          <p className="text-muted-foreground">
            Energie- en audio analyses
          </p>
        </div>
      </div>

      {/* Module Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/crm/analyses/piekvermogen')}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <Zap className="h-5 w-5 text-primary" />
              </div>
              <CardTitle className="text-lg">Piekvermogen</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <CardDescription>
              Analyseer kwartierdata om piekverbruiken te visualiseren en vergelijken met gecontracteerd vermogen.
            </CardDescription>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/crm/analyses/transcripties')}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Mic className="h-5 w-5 text-purple-500" />
              </div>
              <CardTitle className="text-lg">Transcripties</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <CardDescription>
              Converteer audio-opnames naar tekst met AI-analyse en actiepuntenextractie.
            </CardDescription>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/crm/analyses/epse')}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-green-500/10">
                <Battery className="h-5 w-5 text-green-500" />
              </div>
              <CardTitle className="text-lg">Simulatie (EPSE)</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <CardDescription>
              Energy Profile & Simulation Engine: kwartierdata analyse, profielconstructie en BESS-simulaties.
            </CardDescription>
          </CardContent>
        </Card>

        {/* API Costs Summary Card */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <DollarSign className="h-5 w-5 text-emerald-500" />
              </div>
              <CardTitle className="text-lg">API Kosten</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-mono font-semibold text-emerald-600">
              €{(monthlyCost * 0.92).toFixed(2)}
            </div>
            <p className="text-sm text-muted-foreground">
              Transcripties deze maand
            </p>
          </CardContent>
        </Card>
      </div>

      {/* History Section */}
      <Card>
        <CardHeader>
          <CardTitle>Analyse Historie</CardTitle>
          <CardDescription>
            Alle analyses op één plek
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search & Bulk Actions */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Zoek analyses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            {selectedPiekIds.length > 0 && (
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Verwijder ({selectedPiekIds.length})
              </Button>
            )}
          </div>

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedIds.length === filteredAnalyses.length && filteredAnalyses.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Naam</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Aangemaakt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Laden...
                    </TableCell>
                  </TableRow>
                ) : filteredAnalyses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Geen analyses gevonden
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAnalyses.map((analysis) => (
                    <TableRow 
                      key={`${analysis.type}-${analysis.id}`}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(analysis.route)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.includes(analysis.id)}
                          onCheckedChange={(checked) => handleSelectOne(analysis.id, !!checked)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{analysis.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          analysis.type === 'piekvermogen' 
                            ? 'border-primary/30 text-primary' 
                            : 'border-purple-500/30 text-purple-600'
                        }>
                          {analysis.type === 'piekvermogen' ? (
                            <><Zap className="h-3 w-3 mr-1" /> Piekvermogen</>
                          ) : (
                            <><Mic className="h-3 w-3 mr-1" /> Transcriptie</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {getStatusIcon(analysis.status)}
                          <span className="text-sm capitalize">
                            {analysis.status === 'completed' ? 'Voltooid' : 
                             analysis.status === 'processing' ? 'Verwerken' :
                             analysis.status === 'failed' ? 'Mislukt' : analysis.status}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {analysis.detail}
                      </TableCell>
                      <TableCell>
                        {format(new Date(analysis.created_at), 'd MMM yyyy', { locale: nl })}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Analyses verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je {selectedPiekIds.length} piekvermogen analyse(s) wilt verwijderen? 
              Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-destructive text-destructive-foreground">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CRMAnalyses;
